import React, { useState, useEffect } from 'react';
import {
    Box,
    Paper,
    Typography,
    Button,
    TextField,
    InputAdornment,
    Chip,
    Card,
    CardContent,
    CardActions,
    IconButton,
    Menu,
    MenuItem,
    Tabs,
    Tab,
    FormControl,
    InputLabel,
    Select
} from '@mui/material';
import {
    Search as SearchIcon,
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Visibility as VisibilityIcon,
    MoreVert as MoreVertIcon,
    Public as PublicIcon,
    Business as BusinessIcon,
    History as HistoryIcon,
    Category as CategoryIcon,
    Book as BookIcon,
    Help as HelpIcon,
    Settings as SettingsIcon,
    Info as InfoIcon,
    Warning as WarningIcon,
    Star as StarIcon,
    StarBorder as StarBorderIcon,
    Favorite as FavoriteIcon,
    Home as HomeIcon,
    Work as WorkIcon,
    School as SchoolIcon,
    Computer as ComputerIcon,
    Phone as PhoneIcon,
    Email as EmailIcon,
    Security as SecurityIcon,
    Build as BuildIcon,
    Code as CodeIcon,
    CloudQueue as CloudIcon,
    Storage as StorageIcon,
    Assignment as AssignmentIcon,
    Description as DescriptionIcon,
    Folder as FolderIcon,
    Label as LabelIcon
} from '@mui/icons-material';
import { useDataProvider, useNotify, usePermissions, useGetIdentity, useTranslate } from 'react-admin';
import { useNavigate } from 'react-router-dom';
import { devLog } from '../../utils/devLog';

interface WikiArticle {
    id: number;
    title: string;
    slug: string;
    status: 'draft' | 'published' | 'archived';
    summary?: string;
    companyId?: number;
    createdBy?: number;
    category?: {
        id: number;
        name: string;
        color: string;
        icon?: string;
    } | string; // Can be object or IRI string
    createdAt: string;
    updatedAt: string;
    publishedAt?: string;
    viewCount: number;
}

const iconMap: Record<string, React.ComponentType> = {
    'book': BookIcon,
    'help': HelpIcon,
    'settings': SettingsIcon,
    'info': InfoIcon,
    'warning': WarningIcon,
    'star': StarIcon,
    'favorite': FavoriteIcon,
    'home': HomeIcon,
    'work': WorkIcon,
    'school': SchoolIcon,
    'computer': ComputerIcon,
    'phone': PhoneIcon,
    'email': EmailIcon,
    'security': SecurityIcon,
    'build': BuildIcon,
    'code': CodeIcon,
    'cloud_queue': CloudIcon,
    'storage': StorageIcon,
    'assignment': AssignmentIcon,
    'description': DescriptionIcon,
    'folder': FolderIcon,
    'label': LabelIcon
};

const Wiki: React.FC = () => {
    const [articles, setArticles] = useState<WikiArticle[]>([]);
    const [categories, setCategories] = useState<Map<number, { name: string; color: string; icon?: string }>>(new Map());
    const [searchQuery, setSearchQuery] = useState('');
    const [statusFilter, setStatusFilter] = useState<'all' | 'draft' | 'published' | 'archived'>('all');
    const [categoryFilter, setCategoryFilter] = useState<number | 'all'>('all');
    const [showFavoritesOnly, setShowFavoritesOnly] = useState(false);
    const [favoriteArticleIds, setFavoriteArticleIds] = useState<Set<number>>(new Set());
    const [loading, setLoading] = useState(false);
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const [selectedArticle, setSelectedArticle] = useState<WikiArticle | null>(null);

    const dataProvider = useDataProvider();
    const notify = useNotify();
    const navigate = useNavigate();
    const { permissions } = usePermissions();
    const { data: identity } = useGetIdentity();
    const translate = useTranslate();

    // docs/user/features/wiki.md: "Artikel anlegen/bearbeiten dürfen: Editor,
    // Teacher, Instructor, Content-Manager, Company-Admin". Frontend-Gate
    // folgt dem jetzt 1:1 zur API-Gate-Liste.
    const canCreateArticles =
        permissions?.includes('ROLE_EDITOR') ||
        permissions?.includes('ROLE_TEACHER') ||
        permissions?.includes('ROLE_INSTRUCTOR') ||
        permissions?.includes('ROLE_CONTENT_MANAGER') ||
        permissions?.includes('ROLE_COMPANY_ADMIN') ||
        permissions?.includes('ROLE_PLATFORM_ADMIN') ||
        permissions?.includes('ROLE_OPERATOR');
    const isPlatformAdmin = permissions?.includes('ROLE_PLATFORM_ADMIN') || permissions?.includes('ROLE_OPERATOR');
    const isEditor =
        permissions?.includes('ROLE_EDITOR') ||
        permissions?.includes('ROLE_TEACHER') ||
        permissions?.includes('ROLE_INSTRUCTOR') ||
        permissions?.includes('ROLE_CONTENT_MANAGER') ||
        permissions?.includes('ROLE_COMPANY_ADMIN');

    const canEditArticle = (article: WikiArticle) => {
        const isAuthor = identity?.id && article.createdBy && identity.id === article.createdBy;
        return isAuthor || isEditor || isPlatformAdmin;
    };

    useEffect(() => {
        loadCategories();
        loadFavorites();
    }, [identity]);

    useEffect(() => {
        loadArticles();
    }, [searchQuery, statusFilter, categoryFilter]);

    const loadFavorites = async () => {
        if (!identity?.id) return;

        try {
            // Ensure userId is a number for the API filter
            const userId = typeof identity.id === 'string' ? parseInt(identity.id, 10) : identity.id;

            // Get all favorites for this user
            const { data } = await dataProvider.getList('wiki_favorites', {
                pagination: { page: 1, perPage: 1000 },
                sort: { field: 'id', order: 'DESC' },
                filter: { userId: userId }
            });

            devLog('[Wiki] Loaded favorites for user', identity.id, ':', data);

            const favoriteIds = new Set<number>();
            data.forEach((fav: any) => {
                // Extract article ID from various possible formats
                let articleId: number | null = null;

                // Direct articleId property
                if (fav.articleId) {
                    articleId = typeof fav.articleId === 'number' ? fav.articleId : parseInt(fav.articleId, 10);
                }
                // article as plain number or numeric string (dataProvider already converted IRI)
                else if (fav.article && (typeof fav.article === 'number' || (typeof fav.article === 'string' && /^\d+$/.test(fav.article)))) {
                    articleId = typeof fav.article === 'number' ? fav.article : parseInt(fav.article, 10);
                }
                // article as IRI string like /api/wiki_articles/8
                else if (fav.article && typeof fav.article === 'string') {
                    const match = fav.article.match(/\/(\d+)$/);
                    if (match) {
                        articleId = parseInt(match[1], 10);
                    }
                }
                // article as object with id
                else if (fav.article && typeof fav.article === 'object') {
                    if (fav.article.id) {
                        articleId = typeof fav.article.id === 'number' ? fav.article.id : parseInt(fav.article.id, 10);
                    } else if (fav.article['@id']) {
                        const match = fav.article['@id'].match(/\/(\d+)$/);
                        if (match) {
                            articleId = parseInt(match[1], 10);
                        }
                    }
                }

                if (articleId && !isNaN(articleId)) {
                    favoriteIds.add(articleId);
                    devLog('[Wiki] Found favorite article ID:', articleId);
                }
            });

            devLog('[Wiki] Total favorite article IDs:', favoriteIds);
            setFavoriteArticleIds(favoriteIds);
        } catch (error) {
            console.error('Error loading favorites:', error);
        }
    };

    const loadCategories = async () => {
        try {
            const { data } = await dataProvider.getList('wiki_categories', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'name', order: 'ASC' },
                filter: {}
            });
            const categoryMap = new Map();
            data.forEach((cat: any) => {
                categoryMap.set(cat.id, { name: cat.name, color: cat.color, icon: cat.icon });
            });
            setCategories(categoryMap);
        } catch (error) {
            console.error('Error loading categories:', error);
        }
    };

    const loadArticles = async () => {
        setLoading(true);
        try {
            const filter: any = {};

            if (searchQuery) {
                // Search in both title and summary
                filter.title = searchQuery;
                filter.summary = searchQuery;
            }

            if (statusFilter !== 'all') {
                filter.status = statusFilter;
            }

            if (categoryFilter !== 'all') {
                filter.category = `/api/wiki_categories/${categoryFilter}`;
            }

            const { data } = await dataProvider.getList('wiki_articles', {
                pagination: { page: 1, perPage: 100 },
                sort: { field: 'updatedAt', order: 'DESC' },
                filter
            });

            setArticles(data);
        } catch (error) {
            notify(translate('wiki.loadError'), { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    const handleMenuOpen = (event: React.MouseEvent<HTMLElement>, article: WikiArticle) => {
        setAnchorEl(event.currentTarget);
        setSelectedArticle(article);
    };

    const handleMenuClose = () => {
        setAnchorEl(null);
        setSelectedArticle(null);
    };

    const handleView = (article: WikiArticle) => {
        navigate(`/wiki/${article.slug}`);
        handleMenuClose();
    };

    const handleEdit = (article: WikiArticle) => {
        navigate(`/wiki/${article.slug}/edit`);
        handleMenuClose();
    };

    const handleDelete = async (article: WikiArticle) => {
        if (!window.confirm(translate('wiki.confirmDelete', { title: article.title }))) return;

        try {
            await dataProvider.delete('wiki_articles', { id: article.id, previousData: article });
            notify(translate('wiki.deleted'), { type: 'success' });
            loadArticles();
        } catch (error) {
            notify(translate('wiki.deleteError'), { type: 'error' });
        }
        handleMenuClose();
    };

    const getStatusChip = (status: string) => {
        const statusConfig = {
            draft: { label: translate('wiki.status.draft'), color: 'default' as const },
            published: { label: translate('wiki.status.published'), color: 'success' as const },
            archived: { label: translate('wiki.status.archived'), color: 'warning' as const }
        };

        const config = statusConfig[status as keyof typeof statusConfig] || statusConfig.draft;
        return <Chip label={config.label} color={config.color} size="small" />;
    };

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    return (
        <Box>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2 }}>
                    {canCreateArticles && (
                        <>
                            <Button
                                variant="outlined"
                                startIcon={<CategoryIcon />}
                                onClick={() => navigate('/wiki/categories')}
                            >
                                {translate('wiki.categoriesButton')}
                            </Button>
                            <Button
                                variant="contained"
                                startIcon={<AddIcon />}
                                onClick={() => navigate('/wiki/new/edit')}
                            >
                                {translate('wiki.newArticle')}
                            </Button>
                        </>
                    )}
                </Box>
            </Box>

            {/* Search & Filter — flexWrap: bricht auf schmalen Viewports um (#36-Sweep) */}
            <Paper sx={{ p: 2, mb: 3 }}>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2, flexWrap: 'wrap' }}>
                    <TextField
                        placeholder={translate('wiki.searchPlaceholder')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        size="small"
                        sx={{ flexGrow: 1 }}
                        InputProps={{
                            startAdornment: (
                                <InputAdornment position="start">
                                    <SearchIcon />
                                </InputAdornment>
                            )
                        }}
                    />
                    <FormControl size="small" sx={{ minWidth: 200 }}>
                        <InputLabel>{translate('wiki.categoryLabel')}</InputLabel>
                        <Select
                            value={categoryFilter}
                            label={translate('wiki.categoryLabel')}
                            onChange={(e) => setCategoryFilter(e.target.value as number | 'all')}
                        >
                            <MenuItem value="all">{translate('wiki.allCategories')}</MenuItem>
                            {Array.from(categories.entries()).map(([id, cat]) => (
                                <MenuItem key={id} value={id}>
                                    {cat.name}
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    <Button
                        variant={showFavoritesOnly ? 'contained' : 'outlined'}
                        color={showFavoritesOnly ? 'warning' : 'inherit'}
                        startIcon={showFavoritesOnly ? <StarIcon /> : <StarBorderIcon />}
                        onClick={() => setShowFavoritesOnly(!showFavoritesOnly)}
                        sx={{ whiteSpace: 'nowrap' }}
                    >
                        {translate('wiki.favorites', 'Favoriten')}
                        {favoriteArticleIds.size > 0 && ` (${favoriteArticleIds.size})`}
                    </Button>
                </Box>

                <Tabs value={statusFilter} onChange={(e, newValue) => setStatusFilter(newValue)}>
                    <Tab label={translate('wiki.tabs.all')} value="all" />
                    <Tab label={translate('wiki.tabs.drafts')} value="draft" />
                    <Tab label={translate('wiki.tabs.published')} value="published" />
                    <Tab label={translate('wiki.tabs.archived')} value="archived" />
                </Tabs>
            </Paper>

            {/* Articles Grid */}
            {loading ? (
                <Typography>{translate('wiki.loading')}</Typography>
            ) : articles.length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <Typography color="text.secondary">
                        {translate('wiki.noArticles')}
                    </Typography>
                </Paper>
            ) : showFavoritesOnly && articles.filter(a => favoriteArticleIds.has(a.id)).length === 0 ? (
                <Paper sx={{ p: 4, textAlign: 'center' }}>
                    <StarBorderIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
                    <Typography variant="h6" color="text.secondary" gutterBottom>
                        {translate('wiki.noFavorites', 'Keine Favoriten vorhanden')}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        {translate('wiki.noFavoritesHint', 'Markieren Sie Artikel als Favoriten, um sie hier schnell wiederzufinden.')}
                    </Typography>
                </Paper>
            ) : (
                <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(300px, 1fr))', gap: 3 }}>
                    {articles
                        .filter((article) => !showFavoritesOnly || favoriteArticleIds.has(article.id))
                        .map((article) => (
                        <Card key={article.id}>
                            <CardContent>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {getStatusChip(article.status)}
                                        {favoriteArticleIds.has(article.id) && (
                                            <StarIcon fontSize="small" sx={{ color: 'warning.main' }} />
                                        )}
                                    </Box>
                                    <IconButton
                                        size="small"
                                        onClick={(e) => handleMenuOpen(e, article)}
                                    >
                                        <MoreVertIcon />
                                    </IconButton>
                                </Box>

                                <Typography variant="h6" gutterBottom>
                                    {article.title}
                                </Typography>

                                {article.summary && (
                                    <Typography
                                        variant="body2"
                                        color="text.secondary"
                                        sx={{
                                            mb: 2,
                                            overflow: 'hidden',
                                            textOverflow: 'ellipsis',
                                            display: '-webkit-box',
                                            WebkitLineClamp: 3,
                                            WebkitBoxOrient: 'vertical'
                                        }}
                                    >
                                        {article.summary}
                                    </Typography>
                                )}

                                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                                    {(() => {
                                        // If category is already an object with all data, use it directly
                                        if (article.category && typeof article.category === 'object' && article.category.name) {
                                            const IconComponent = article.category.icon ? iconMap[article.category.icon] : null;
                                            return (
                                                <Chip
                                                    label={article.category.name}
                                                    size="small"
                                                    icon={IconComponent ? React.createElement(IconComponent) : undefined}
                                                    sx={{
                                                        backgroundColor: article.category.color,
                                                        color: '#fff',
                                                        '& .MuiChip-icon': { color: '#fff !important' }
                                                    }}
                                                />
                                            );
                                        }

                                        // Otherwise, try to load from categories map
                                        let categoryId: number | null = null;
                                        if (article.category) {
                                            if (typeof article.category === 'string') {
                                                // Try to extract ID from IRI
                                                const match = article.category.match(/\/(\d+)$/);
                                                if (match) {
                                                    categoryId = parseInt(match[1], 10);
                                                } else {
                                                    // Plain ID string like "1"
                                                    const parsed = parseInt(article.category, 10);
                                                    categoryId = isNaN(parsed) ? null : parsed;
                                                }
                                            } else if (typeof article.category === 'object' && article.category.id) {
                                                categoryId = article.category.id;
                                            }
                                        }

                                        const category = categoryId ? categories.get(categoryId) : null;

                                        if (!category) return null;

                                        const IconComponent = category.icon ? iconMap[category.icon] : null;

                                        return (
                                            <Chip
                                                label={category.name}
                                                size="small"
                                                icon={IconComponent ? React.createElement(IconComponent) : undefined}
                                                sx={{
                                                    backgroundColor: category.color,
                                                    color: '#fff',
                                                    '& .MuiChip-icon': { color: '#fff !important' }
                                                }}
                                            />
                                        );
                                    })()}
                                    {article.companyId ? (
                                        <Chip icon={<BusinessIcon />} label={translate('wiki.company')} size="small" />
                                    ) : (
                                        <Chip icon={<PublicIcon />} label={translate('wiki.platform')} size="small" />
                                    )}
                                </Box>

                                <Typography variant="caption" color="text.secondary">
                                    {translate('wiki.updated')}: {formatDate(article.updatedAt)}
                                </Typography>
                            </CardContent>

                            <CardActions>
                                <Button
                                    size="small"
                                    startIcon={<VisibilityIcon />}
                                    onClick={() => handleView(article)}
                                >
                                    {translate('wiki.view')}
                                </Button>
                            </CardActions>
                        </Card>
                    ))}
                </Box>
            )}

            {/* Context Menu */}
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={handleMenuClose}
            >
                <MenuItem onClick={() => selectedArticle && handleView(selectedArticle)}>
                    <VisibilityIcon fontSize="small" sx={{ mr: 1 }} />
                    {translate('wiki.view')}
                </MenuItem>
                {selectedArticle && canEditArticle(selectedArticle) && (
                    <MenuItem onClick={() => selectedArticle && handleEdit(selectedArticle)}>
                        <EditIcon fontSize="small" sx={{ mr: 1 }} />
                        {translate('wiki.edit')}
                    </MenuItem>
                )}
                {selectedArticle && canEditArticle(selectedArticle) && (
                    <MenuItem onClick={() => selectedArticle && handleDelete(selectedArticle)} sx={{ color: 'error.main' }}>
                        <DeleteIcon fontSize="small" sx={{ mr: 1 }} />
                        {translate('wiki.delete')}
                    </MenuItem>
                )}
            </Menu>
        </Box>
    );
};

export default Wiki;
